/**
 * `<bundle-builder>` — flavor-slot picker for `sections/bundle-builder.liquid`.
 * Shoppers add up to `maxItems` flavors; the 2-item / 3-item discount tiers
 * unlock as slots fill, shown by the progress bar at the top of the sidebar
 * (Figma 325:5837). The selected Recharge plan's discount stacks on the tier
 * discount, one cart line per flavor. Recharge gives each product its own
 * selling plan ids, so each item is sent with its own product's plan of the
 * selected delivery frequency — items whose product has no such plan are
 * added as one-time purchases, since Shopify rejects a plan a variant lacks.
 *
 * Adding to cart dispatches the same `CartLinesUpdateEvent` as Horizon's
 * product form, so the cart drawer re-renders and opens (or Rebuy Smart Cart
 * opens instead, see rebuy-smart-cart.js) and the header count updates.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.maxItems = Number(this.dataset.maxItems);
    this.tierDiscounts = JSON.parse(this.dataset.tierDiscounts || '{}');
    this.moneyFormat = this.dataset.moneyFormat;
    this.cartAddUrl = this.dataset.cartAddUrl;
    this.cartUrl = this.dataset.cartUrl;
    this.removeIcon = this.dataset.removeIcon || '&times;';

    /** @type {Array<{variantId: string, price: number, title: string, image: string, plans: Map<string, {id: string, price: number}>, tierEligible: boolean}>} */
    this.items = [];
    this.isSubmitting = false;

    this.slotsContainer = this.querySelector('.bundle-builder__slots');
    this.slotTemplate = this.querySelector('.bundle-builder__slot-template');
    this.addToCartButton = this.querySelector('.bundle-builder__add-to-cart');
    this.progressCount = this.querySelector('.bundle-builder__progress-count');

    this.querySelectorAll('.bundle-builder__add-button').forEach((button) => {
      button.addEventListener('click', () => this.#addToNextSlot(button));
    });

    this.querySelectorAll('.bundle-builder__frequency-radio, .bundle-builder__plan-select').forEach((input) => {
      input.addEventListener('change', () => this.#recalculate());
    });

    this.addToCartButton?.addEventListener('click', () => this.#submitBundle());

    this.#renderSlots();
    this.#recalculate();
  }

  #addToNextSlot(button) {
    if (this.items.length >= this.maxItems) return;

    this.items.push({
      variantId: button.dataset.variantId,
      price: Number(button.dataset.price),
      title: button.dataset.title,
      image: button.dataset.image,
      // Keyed by delivery frequency; the first plan per frequency wins.
      plans: new Map(
        (button.dataset.planPrices || '')
          .split(',')
          .filter(Boolean)
          .map((entry) => {
            const [key, id, price] = entry.split(':');
            return /** @type {[string, {id: string, price: number}]} */ ([key, { id, price: Number(price) }]);
          })
          .reverse()
      ),
      tierEligible: button.dataset.tierEligible !== 'false',
    });

    this.#renderSlots();
    this.#recalculate();
  }

  #removeSlot(index) {
    this.items.splice(index, 1);
    this.#renderSlots();
    this.#recalculate();
  }

  #renderSlots() {
    if (!this.slotsContainer || !this.slotTemplate) return;
    this.slotsContainer.replaceChildren();

    this.items.forEach((item, i) => {
      const slot = document.createElement('div');
      slot.className = 'bundle-builder__slot';
      slot.innerHTML = `
        <div class="bundle-builder__slot-media">${
          item.image ? `<img src="${item.image}" alt="${item.title}" loading="lazy">` : ''
        }</div>
        <p class="bundle-builder__slot-title">${item.title}</p>
        <button type="button" class="bundle-builder__slot-remove" aria-label="Remove ${item.title}">${this.removeIcon}</button>
      `;
      slot.querySelector('.bundle-builder__slot-remove')?.addEventListener('click', () => this.#removeSlot(i));
      this.slotsContainer.appendChild(slot);
    });

    // Remaining empty slots; only the next one is labelled, with the tier it unlocks.
    for (let i = this.items.length; i < this.maxItems; i++) {
      const slot = /** @type {HTMLElement} */ (this.slotTemplate.firstElementChild.cloneNode(true));

      if (i === this.items.length) {
        const nextCount = i + 1;
        const nextDiscount = this.tierDiscounts[String(nextCount)];
        slot.querySelector('.bundle-builder__slot-title').textContent = 'Select a Product';
        slot.querySelector('.bundle-builder__slot-subtitle').textContent = nextDiscount
          ? `Buy ${nextCount} and unlock ${nextDiscount}% off`
          : '';
      }

      this.slotsContainer.appendChild(slot);
    }
  }

  /** Items the store's pack discounts apply to — only these count toward a tier. */
  #eligibleCount() {
    return this.items.filter((item) => item.tierEligible).length;
  }

  #updateProgress() {
    const count = this.items.length;
    if (this.progressCount) this.progressCount.textContent = `${count}/${this.maxItems}`;

    const eligibleCount = this.#eligibleCount();
    this.querySelectorAll('.bundle-builder__milestone').forEach((milestone) => {
      milestone.classList.toggle('is-reached', eligibleCount >= Number(milestone.dataset.threshold));
    });
  }

  #tierDiscount() {
    const eligibleCount = this.#eligibleCount();
    let discount = 0;
    for (const [threshold, percent] of Object.entries(this.tierDiscounts)) {
      if (eligibleCount >= Number(threshold)) discount = Math.max(discount, Number(percent));
    }
    return discount;
  }

  #formatMoney(cents) {
    return window.Shopify?.formatMoney ? window.Shopify.formatMoney(cents, this.moneyFormat) : `$${(cents / 100).toFixed(2)}`;
  }

  #isSubscribing() {
    return this.querySelector('.bundle-builder__frequency-radio[value="subscribe"]')?.checked ?? false;
  }

  /** Delivery-frequency key of the plan picked in the sidebar select. */
  #selectedPlanKey() {
    const select = /** @type {HTMLSelectElement | null} */ (this.querySelector('.bundle-builder__plan-select'));
    return select?.selectedOptions[0]?.dataset.planKey ?? null;
  }

  /** The item's own selling plan for the selected frequency, if its product offers one. */
  #planFor(item) {
    const key = this.#selectedPlanKey();
    return key ? item.plans.get(key) : undefined;
  }

  #recalculate() {
    this.#updateProgress();

    const subtotal = this.items.reduce((sum, item) => sum + item.price, 0);
    const tierPercent = this.#tierDiscount();

    // Mirror Shopify's cart math so the sidebar matches the drawer to the
    // cent (checked against /cart.js): the store's automatic pack discount
    // only covers tier-eligible products, is taken per unit and rounded down
    // to a whole cent (15% of $24.99 = $3.74 off, not $3.75), and stacks on
    // Shopify's own per-variant plan price, which only applies to items whose
    // product offers the selected plan.
    const discountedLine = (/** @type {number} */ cents, /** @type {boolean} */ eligible) =>
      eligible ? cents - Math.floor((cents * tierPercent) / 100) : cents;
    const oneTimePrice = this.items.reduce((sum, item) => sum + discountedLine(item.price, item.tierEligible), 0);
    const subscribePrice = this.items.reduce(
      (sum, item) => sum + discountedLine(this.#planFor(item)?.price ?? item.price, item.tierEligible),
      0
    );
    const finalPrice = this.#isSubscribing() ? subscribePrice : oneTimePrice;

    const setText = (selector, cents) => {
      const el = this.querySelector(selector);
      if (el) el.textContent = this.#formatMoney(cents);
    };
    setText('[data-price-role="one-time-final"]', oneTimePrice);
    setText('[data-price-role="subscribe-original"]', oneTimePrice);
    setText('[data-price-role="subscribe-final"]', subscribePrice);

    const subscribeOriginalEl = this.querySelector('[data-price-role="subscribe-original"]');
    if (subscribeOriginalEl) subscribeOriginalEl.hidden = oneTimePrice === subscribePrice;

    const totalEl = this.querySelector('.bundle-builder__total-price');
    if (totalEl) totalEl.textContent = this.#formatMoney(finalPrice);

    const originalEl = this.querySelector('.bundle-builder__original-price');
    if (originalEl) {
      originalEl.textContent = this.#formatMoney(subtotal);
      originalEl.hidden = subtotal === finalPrice;
    }

    if (this.addToCartButton) {
      this.addToCartButton.disabled = this.items.length === 0;
    }
  }

  async #submitBundle() {
    if (this.isSubmitting) return;

    const filled = this.items;
    if (filled.length === 0) return;

    const subscribing = this.#isSubscribing();

    const items = filled.map((item) => {
      const cartItem = {
        id: Number(item.variantId),
        quantity: 1,
        // Underscore prefix keeps it on the order but hides it in cart/checkout.
        properties: { '_Pack Size': `${filled.length}-Pack` },
      };
      const plan = subscribing ? this.#planFor(item) : undefined;
      if (plan) cartItem.selling_plan = Number(plan.id);
      return cartItem;
    });

    this.isSubmitting = true;
    this.classList.add('is-loading');
    if (this.addToCartButton) this.addToCartButton.disabled = true;

    // Stores set to a page cart (no drawer, no Rebuy) keep the old redirect.
    const hasCartOverlay =
      document.querySelector('cart-drawer-component') || document.body.hasAttribute('data-rebuy-smart-cart');

    // Ask Shopify to render the drawer's cart sections in the add response,
    // exactly as product-form.js does, so the drawer morphs without a refetch.
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    if (hasCartOverlay) {
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
          promise: deferred.promise,
        })
      );
    }

    try {
      const response = await fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.message || data.description || 'Something went wrong adding this bundle to your cart.');
      }

      if (!hasCartOverlay) {
        window.location.href = this.cartUrl || '/cart';
        return;
      }

      await this.#resolveCartEvent(deferred, { sections: data.sections, didError: false, itemCount: items.length });
    } catch (error) {
      // Like product-form.js: resolve with didError so the drawer/header
      // settle on the real cart instead of treating it as a rejected event.
      if (hasCartOverlay) {
        this.#resolveCartEvent(deferred, { didError: true, itemCount: items.length }).catch(deferred.reject);
      }
      console.error('[bundle-builder] Failed to add bundle to cart:', error);
      window.alert(error instanceof Error ? error.message : 'Something went wrong adding this bundle to your cart.');
    } finally {
      this.isSubmitting = false;
      this.classList.remove('is-loading');
      this.#recalculate();
    }
  }

  /**
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} deferred
   * @param {{ sections?: Record<string, string>, didError: boolean, itemCount: number }} detail
   */
  async #resolveCartEvent(deferred, detail) {
    const cart = await fetch(`${this.cartUrl || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then(
      (response) => response.json()
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { ...detail, items: cart.items, source: 'bundle-builder', sourceId: this.id },
    });
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
